import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  buildAuthBypassHeaders,
  setAuthBypassOptOut,
} from '../lib/authBypass.js';
import {
  captureClientError,
  identifyPostHogUser,
  resetPostHogUser,
} from '../lib/posthog.js';
import { queryClient } from '../lib/query/client.js';
import { queryKeys } from '../lib/query/keys.js';
import {
  configureQueryCachePersistence,
  teardownQueryCachePersistence,
} from '../lib/query/persistence.js';
import { apiUrl } from '../utils/api.js';
import { getCheckoutReturnToken, type BillingSnapshotResponse } from './useBillingSnapshot.js';

type AuthUser = {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  emailVerified: boolean;
};

export type MarketingConsentState = {
  consentGranted: boolean;
  consentedAt: string | null;
  withdrawnAt: string | null;
  source: string;
  policyVersion: string;
} | null;

type AuthSession = {
  id: string;
  userId: string;
  expiresAt: string;
};

type AuthState = {
  user: AuthUser | null;
  session: AuthSession | null;
  marketingConsent: MarketingConsentState;
  isBypassSession: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

async function fetchMe() {
  const response = await fetch(apiUrl('/me'), {
    credentials: 'include',
    headers: buildAuthBypassHeaders(),
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to load session: HTTP ${response.status}`);
  }

  const data = (await response.json()) as {
    user: AuthUser;
    session: AuthSession;
    marketingConsent?: MarketingConsentState;
    isBypassSession?: boolean;
  };

  return data;
}

function delay(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export type FetchMeResult = {
  user: AuthUser;
  session: AuthSession;
  marketingConsent?: MarketingConsentState;
  isBypassSession?: boolean;
} | null;

/**
 * Retry wrapper for /me.
 *
 * The OAuth callback path can race with session-cookie persistence in some
 * edge/proxy paths: the SPA calls /me before the Set-Cookie has committed and
 * gets a 401. We retry a few times with a short backoff before treating
 * unauthenticated as final.
 *
 * Exported for testing. Accepts fetcher + delay overrides so tests can drive
 * the retry ladder deterministically under fake timers without touching the
 * global fetch.
 */
export async function fetchMeWithRetryCore(
  fetcher: () => Promise<FetchMeResult>,
  sleep: (ms: number) => Promise<void> = delay,
  maxAttempts = 4,
): Promise<FetchMeResult> {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const data = await fetcher();

      if (data === null && attempt < maxAttempts) {
        await sleep(150 * attempt);
        continue;
      }

      return data;
    } catch (error) {
      if (attempt >= maxAttempts) {
        throw error;
      }
      await sleep(200 * attempt);
    }
  }

  return null;
}

export function shouldRetryFetchMeForCurrentLocation(
  locationLike: Pick<Location, 'pathname' | 'search'> | null | undefined,
): boolean {
  if (!locationLike) {
    return false;
  }

  if (getCheckoutReturnToken(locationLike.search) === 'auth_return') {
    return true;
  }

  return locationLike.pathname.startsWith('/auth');
}

async function fetchMeWithRetry(): Promise<FetchMeResult> {
  const shouldRetry =
    typeof window !== 'undefined' && shouldRetryFetchMeForCurrentLocation(window.location);
  return fetchMeWithRetryCore(fetchMe, delay, shouldRetry ? 4 : 1);
}

let hasPrefetchedBillingSnapshot = false;

/**
 * The protected bootstrap used to be a waterfall: /me resolves, then the
 * billing snapshot loads, then page data. Fire the snapshot request in
 * parallel with /me and seed the query cache once the user id is known.
 *
 * Uses the cached D1 snapshot path so a cold dashboard entry does not block on
 * live billing verification. Skipped on checkout returns, where the route-level
 * flow must force its own refresh.
 */
function startBillingSnapshotPrefetch(): Promise<BillingSnapshotResponse | null> | null {
  if (hasPrefetchedBillingSnapshot || typeof window === 'undefined') {
    return null;
  }

  if (getCheckoutReturnToken(window.location.search)) {
    return null;
  }

  hasPrefetchedBillingSnapshot = true;
  return fetch(apiUrl('/billing/subscription'), {
    credentials: 'include',
    headers: buildAuthBypassHeaders(),
  })
    .then((response) =>
      response.ok ? (response.json() as Promise<BillingSnapshotResponse>) : null,
    )
    .catch(() => null);
}

function buildSignOutTargets(): string[] {
  const targets = new Set<string>([apiUrl('/auth/sign-out')]);

  if (typeof window !== 'undefined') {
    targets.add(`${window.location.origin}/api/auth/sign-out`);
  }

  return Array.from(targets);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [marketingConsent, setMarketingConsent] = useState<MarketingConsentState>(null);
  const [isBypassSession, setIsBypassSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    const billingPrefetch = startBillingSnapshotPrefetch();
    try {
      const data = await fetchMeWithRetry();
      if (data?.user && data.isBypassSession !== true) {
        configureQueryCachePersistence(queryClient, data.user.id);
      } else {
        teardownQueryCachePersistence();
        queryClient.clear();
      }

      setUser(data?.user ?? null);
      setSession(data?.session ?? null);
      setMarketingConsent(data?.marketingConsent ?? null);
      setIsBypassSession(data?.isBypassSession === true);

      if (billingPrefetch && data?.user && data.isBypassSession !== true) {
        const userId = data.user.id;
        void billingPrefetch.then((snapshot) => {
          if (!snapshot) {
            return;
          }

          queryClient.setQueryData<BillingSnapshotResponse>(
            queryKeys.billingSnapshot(userId),
            (existing) => existing ?? snapshot,
          );
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthBypassOptOut();

    const targets = buildSignOutTargets();
    await Promise.allSettled(
      targets.map((url) =>
        fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...buildAuthBypassHeaders(),
          },
          body: '{}',
        }),
      ),
    );

    setUser(null);
    setSession(null);
    setMarketingConsent(null);
    setIsBypassSession(false);
    teardownQueryCachePersistence();
    queryClient.clear();

    // Confirm session is gone on the primary API endpoint.
    const remaining = await fetchMe();
    if (remaining?.user) {
      throw new Error('Sign out did not clear the active session. Please try again.');
    }
  }, []);

  useEffect(() => {
    refresh().catch((error) => {
      captureClientError(error, {
        tags: {
          scope: 'auth',
          operation: 'refresh-session',
        },
      });
      setUser(null);
      setSession(null);
      setMarketingConsent(null);
      setIsBypassSession(false);
      setIsLoading(false);
    });
  }, [refresh]);

  useEffect(() => {
    if (!user) {
      resetPostHogUser();
      return;
    }

    identifyPostHogUser(user.id, {
      email: user.email,
      name: user.name,
      email_verified: user.emailVerified,
      auth_bypass_session: isBypassSession,
      session_id: session?.id,
      session_expires_at: session?.expiresAt,
    });
  }, [user, session, isBypassSession]);

  const value = useMemo(
    () => ({
      user,
      session,
      marketingConsent,
      isBypassSession,
      isLoading,
      refresh,
      signOut,
    }),
    [user, session, marketingConsent, isBypassSession, isLoading, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
